import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { api } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import { CalendarRange, ChevronLeft, ChevronRight, Copy, Wand2 } from 'lucide-react';

const DIAS = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];

const isoLunesDe = (d) => {
  const day = d.getDay(); // 0=Dom
  const diff = (day === 0 ? -6 : 1 - day);
  const l = new Date(d); l.setDate(d.getDate() + diff); l.setHours(0, 0, 0, 0);
  return l;
};
const fmtISO = (d) => d.toISOString().slice(0, 10);
const addDays = (d, n) => { const c = new Date(d); c.setDate(d.getDate() + n); return c; };

const CuadranteCard = () => {
  const [lunes, setLunes] = useState(isoLunesDe(new Date()));
  const [turnos, setTurnos] = useState([]);
  const [empleados, setEmpleados] = useState([]);
  const [asig, setAsig] = useState({}); // key: `${username}|${fecha}` => {id, turno_id, notas}
  const [loading, setLoading] = useState(false);

  const dias = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(lunes, i)), [lunes]);
  const desde = fmtISO(dias[0]);
  const hasta = fmtISO(dias[6]);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/cuadrante', { params: { desde, hasta } });
      setTurnos(r.data.turnos || []);
      setEmpleados(r.data.empleados || []);
      const map = {};
      for (const a of r.data.asignaciones || []) {
        map[`${a.username}|${a.fecha}`] = a;
      }
      setAsig(map);
    } catch (err) {
      toast.error('Error al cargar el cuadrante');
    } finally { setLoading(false); }
  }, [desde, hasta]);

  useEffect(() => { cargar(); }, [cargar]);

  const semanaAnterior = () => setLunes(addDays(lunes, -7));
  const semanaSiguiente = () => setLunes(addDays(lunes, 7));

  const asignar = async (username, fecha, turno_id) => {
    try {
      await api.post('/admin/cuadrante/asignar', {
        username, fecha, turno_id: turno_id === 'ninguno' ? null : turno_id,
      });
      cargar();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const copiarSemana = async () => {
    const destino = addDays(lunes, 7);
    try {
      const r = await api.post('/admin/cuadrante/copiar-semana', {
        desde_lunes: fmtISO(lunes), hasta_lunes: fmtISO(destino),
      });
      toast.success(`Copiado a la semana siguiente: ${r.data.creados} nuevos, ${r.data.actualizados} sobrescritos`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const rotarSemanaSiguiente = async () => {
    // Rotación circular: cada empleado recibe el turno del siguiente
    if (empleados.length < 2 || turnos.length === 0) {
      toast.info('Se necesitan al menos 2 empleados y 1 turno para rotar.');
      return;
    }
    const asignaciones = [];
    const destino = addDays(lunes, 7);
    for (let d = 0; d < 7; d++) {
      const fechaOrigen = fmtISO(addDays(lunes, d));
      const fechaDestino = fmtISO(addDays(destino, d));
      empleados.forEach((emp, idx) => {
        const nextEmp = empleados[(idx + 1) % empleados.length];
        const src = asig[`${nextEmp.username}|${fechaOrigen}`];
        asignaciones.push({
          username: emp.username,
          fecha: fechaDestino,
          turno_id: src?.turno_id || null,
          notas: '',
        });
      });
    }
    try {
      const r = await api.post('/admin/cuadrante/bulk', { asignaciones });
      toast.success(`Rotación aplicada a la semana siguiente: ${r.data.creados} nuevos, ${r.data.actualizados} sobrescritos`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const turnoLabel = (id) => {
    const t = turnos.find((x) => x.id === id);
    return t ? `${t.nombre} (${t.hora_inicio}-${t.hora_fin})` : '';
  };
  const turnoCorto = (id) => {
    const t = turnos.find((x) => x.id === id);
    return t ? t.nombre : '';
  };

  const rango = `${dias[0].toLocaleDateString('es-ES', { day: '2-digit', month: 'short' })} — ${dias[6].toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })}`;

  return (
    <Card className="border-zinc-200" data-testid="cuadrante-card">
      <CardHeader className="flex flex-row items-center justify-between gap-2 flex-wrap">
        <div>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <CalendarRange className="h-5 w-5" /> Cuadrante semanal
          </CardTitle>
          <p className="text-xs text-zinc-500 mt-1">
            Planifica los turnos por empleado y día. Pulsa una celda para asignar. Usa &quot;Copiar semana&quot; o &quot;Rotar&quot; para generar rotaciones rápidas.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={semanaAnterior} data-testid="cuadrante-prev">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="text-sm font-medium text-zinc-700 min-w-[180px] text-center">{rango}</div>
          <Button variant="outline" size="sm" onClick={semanaSiguiente} data-testid="cuadrante-next">
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={copiarSemana} data-testid="cuadrante-copiar" title="Copiar esta semana a la siguiente">
            <Copy className="h-4 w-4 mr-1" /> Copiar
          </Button>
          <Button variant="outline" size="sm" onClick={rotarSemanaSiguiente} data-testid="cuadrante-rotar" title="Rotación circular en la semana siguiente">
            <Wand2 className="h-4 w-4 mr-1" /> Rotar
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-zinc-50">
              <tr className="text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">
                <th className="py-2.5 px-4 sticky left-0 bg-zinc-50">Empleado</th>
                {dias.map((d, i) => (
                  <th key={i} className="py-2.5 px-2 text-center">
                    <div>{DIAS[i]}</div>
                    <div className="text-[10px] font-normal text-zinc-400">{d.getDate()}/{d.getMonth() + 1}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {empleados.length === 0 && (
                <tr><td colSpan={8} className="py-8 text-center text-zinc-400 text-sm">
                  {loading ? 'Cargando...' : 'No hay empleados para mostrar.'}
                </td></tr>
              )}
              {empleados.map((emp) => (
                <tr key={emp.username} className="border-t border-zinc-100" data-testid={`cuadrante-row-${emp.username}`}>
                  <td className="py-2 px-4 font-medium text-sm sticky left-0 bg-white whitespace-nowrap">
                    {emp.full_name}
                    <div className="text-[10px] text-zinc-400">@{emp.username}</div>
                  </td>
                  {dias.map((d, i) => {
                    const fecha = fmtISO(d);
                    const a = asig[`${emp.username}|${fecha}`];
                    return (
                      <td key={i} className="py-1 px-1 text-center align-middle">
                        <Select value={a?.turno_id || 'ninguno'} onValueChange={(v) => asignar(emp.username, fecha, v)}>
                          <SelectTrigger className="h-8 text-[11px] px-2 min-w-[92px]" data-testid={`cuadrante-select-${emp.username}-${fecha}`}>
                            <SelectValue placeholder="—">
                              {a?.turno_id ? turnoCorto(a.turno_id) : '—'}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="ninguno">— Sin turno (descanso)</SelectItem>
                            {turnos.map((t) => (
                              <SelectItem key={t.id} value={t.id}>
                                {turnoLabel(t.id)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {turnos.length === 0 && (
          <div className="p-3 text-xs text-amber-700 bg-amber-50 border-t border-amber-200">
            No has creado turnos aún. Crea al menos uno arriba antes de asignar en el cuadrante.
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CuadranteCard;
